import React from 'react';
import { TylerState, updateReact, useTylerStateUpdate } from '../../utils/TylerState';
import { Currency, FXRate, CURRENCIES } from '../../types/personalForecast';

const FXManager: React.FC = () => {
  useTylerStateUpdate();
  const fxRates = TylerState.forecast.fxRates;
  const accounts = TylerState.forecast.accounts;
  const baseCurrency = TylerState.forecast.settings.baseCurrency;

  // Auto-detect needed pairs: any account currency != base currency
  const neededCurrencies = new Set<Currency>();
  accounts.forEach(a => {
    if (a.currency !== baseCurrency) neededCurrencies.add(a.currency);
  });

  const updateRate = (from: Currency, to: Currency, rate: number) => {
    const idx = fxRates.findIndex(r => r.fromCurrency === from && r.toCurrency === to);
    if (idx >= 0) {
      fxRates[idx].rate = rate;
      fxRates[idx].asOfDate = new Date().toISOString().slice(0, 10);
    } else {
      TylerState.forecast.fxRates.push({
        fromCurrency: from,
        toCurrency: to,
        rate,
        asOfDate: new Date().toISOString().slice(0, 10),
      });
    }
    TylerState.forecast.isDirty = true;
    updateReact();
  };

  const getRate = (from: Currency, to: Currency): number => {
    const r = fxRates.find(r => r.fromCurrency === from && r.toCurrency === to);
    return r?.rate || 1;
  };

  const addCustomPair = () => {
    const available = CURRENCIES.filter(c => c !== baseCurrency && !neededCurrencies.has(c));
    if (available.length === 0) return;
    updateRate(available[0], baseCurrency, 1);
  };

  return (
    <div>
      <h2 style={{ marginBottom: '1rem' }}>FX Rates</h2>
      <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>
        Exchange rates for converting foreign currency accounts to {baseCurrency}.
      </p>

      {neededCurrencies.size === 0 && fxRates.length === 0 ? (
        <div style={{ padding: '2rem', color: '#94a3b8', textAlign: 'center' }}>
          <p>All accounts use {baseCurrency}. No FX rates needed.</p>
          <p style={{ fontSize: '0.9rem' }}>Add accounts in other currencies to see FX rate inputs here.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="accounts-table">
            <thead>
              <tr>
                <th>From</th>
                <th>To</th>
                <th style={{ textAlign: 'right' }}>Rate</th>
                <th>As Of</th>
                <th style={{ width: '80px' }}></th>
              </tr>
            </thead>
            <tbody>
              {Array.from(neededCurrencies).map(currency => (
                <tr key={currency}>
                  <td>{currency}</td>
                  <td>{baseCurrency}</td>
                  <td>
                    <input
                      type="number"
                      value={getRate(currency, baseCurrency)}
                      onChange={e => updateRate(currency, baseCurrency, parseFloat(e.target.value) || 1)}
                      step="0.01"
                      style={{ width: '100px', padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px', textAlign: 'right' }}
                    />
                  </td>
                  <td style={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                    {fxRates.find(r => r.fromCurrency === currency && r.toCurrency === baseCurrency)?.asOfDate || 'Not set'}
                  </td>
                  <td />
                </tr>
              ))}
              {/* Show any extra manually-added pairs */}
              {fxRates
                .filter(r => !neededCurrencies.has(r.fromCurrency))
                .map(r => (
                  <tr key={`${r.fromCurrency}-${r.toCurrency}`}>
                    <td>{r.fromCurrency}</td>
                    <td>{r.toCurrency}</td>
                    <td>
                      <input
                        type="number"
                        value={r.rate}
                        onChange={e => updateRate(r.fromCurrency, r.toCurrency, parseFloat(e.target.value) || 1)}
                        step="0.01"
                        style={{ width: '100px', padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px', textAlign: 'right' }}
                      />
                    </td>
                    <td style={{ color: '#94a3b8', fontSize: '0.9rem' }}>{r.asOfDate}</td>
                    <td>
                      <button
                        onClick={() => {
                          TylerState.forecast.fxRates = fxRates.filter(x => !(x.fromCurrency === r.fromCurrency && x.toCurrency === r.toCurrency));
                          TylerState.forecast.isDirty = true;
                          updateReact();
                        }}
                        style={{ padding: '2px 8px', background: 'none', color: '#94a3b8', border: 'none', cursor: 'pointer', fontSize: '1.1rem' }}
                      >
                        x
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      <button
        className="btn-secondary"
        onClick={addCustomPair}
        style={{ marginTop: '1rem', padding: '6px 16px', fontSize: '0.9rem' }}
      >
        + Add Currency Pair
      </button>
    </div>
  );
};

export default FXManager;
